import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  BarChart3, Users, CreditCard, Building2, FileText, Settings, 
  Menu, X, Bell, User, LogOut, Home, ChevronDown, Search,
  DollarSign, TrendingUp, Calendar, Shield, Database, Globe,
  Image, ArrowUpDown, Gift
} from 'lucide-react';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: BarChart3,
      current: location.pathname === '/admin'
    },
    {
      name: 'Başvuru Yönetimi',
      href: '/admin/applications',
      icon: FileText,
      current: location.pathname === '/admin/applications'
    },
    {
      name: 'İçerik Yönetimi',
      icon: Globe,
      current: location.pathname.startsWith('/admin/content'),
      children: [
        {
          name: 'Banka Yönetimi',
          href: '/admin/content/banks',
          current: location.pathname === '/admin/content/banks'
        },
        {
          name: 'Ürün Yönetimi',
          href: '/admin/content/products',
          current: location.pathname === '/admin/content/products'
        },
        {
          name: 'Banka Kampanyaları',
          href: '/admin/content/campaigns',
          current: location.pathname === '/admin/content/campaigns'
        },
        {
          name: 'Sayfa İçerikleri',
          href: '/admin/content/pages',
          current: location.pathname === '/admin/content/pages'
        },
        {
          name: 'Reklam Yönetimi',
          href: '/admin/content/advertisements',
          current: location.pathname === '/admin/content/advertisements'
        },
        {
          name: 'Sıralama Yönetimi',
          href: '/admin/content/order',
          current: location.pathname === '/admin/content/order'
        }
      ]
    },
    {
      name: 'Kullanıcı Yönetimi',
      href: '/admin/users',
      icon: Users,
      current: location.pathname === '/admin/users'
    },
    {
      name: 'Raporlar',
      href: '/admin/reports',
      icon: TrendingUp,
      current: location.pathname === '/admin/reports'
    },
    {
      name: 'Sistem Ayarları',
      href: '/admin/settings',
      icon: Settings,
      current: location.pathname === '/admin/settings'
    }
  ];

  const [expandedMenus, setExpandedMenus] = useState<string[]>(['İçerik Yönetimi']);

  const toggleMenu = (menuName: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuName) 
        ? prev.filter(name => name !== menuName)
        : [...prev, menuName]
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Fixed position to prevent pushing content */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-200">
            <CreditCard size={32} className="text-[#F7BA45]" />
            <span className="text-xl font-bold text-slate-800">KREDİPARK</span>
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full ml-auto">Admin</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.children ? (
                  <div>
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        item.current
                          ? 'bg-[#F7BA45] text-slate-900'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon size={20} />
                        <span>{item.name}</span>
                      </div>
                      <ChevronDown 
                        size={16} 
                        className={`transition-transform ${
                          expandedMenus.includes(item.name) ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedMenus.includes(item.name) && (
                      <div className="mt-2 ml-6 space-y-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                              child.current
                                ? 'bg-[#F7BA45]/20 text-[#F7BA45] font-medium'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      item.current
                        ? 'bg-[#F7BA45] text-slate-900'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    <item.icon size={20} />
                    <span>{item.name}</span>
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* User Menu */}
          <div className="p-4 border-t border-slate-200">
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-[#F7BA45] rounded-full flex items-center justify-center">
                  <User size={16} className="text-white" />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-slate-900">Admin User</div>
                  <div className="text-xs text-slate-500">admin@kredipark.com</div>
                </div>
                <ChevronDown size={16} />
              </button>

              {userMenuOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg py-2">
                  <Link
                    to="/"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  >
                    <Home size={16} />
                    <span>Ana Siteye Dön</span>
                  </Link>
                  <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100">
                    <LogOut size={16} />
                    <span>Çıkış Yap</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content - With proper left margin to account for fixed sidebar */}
      <div className="lg:ml-64">
        {/* Top bar for mobile */}
        <div className="sticky top-0 z-30 bg-white border-b border-slate-200 lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2">
              <CreditCard size={24} className="text-[#F7BA45]" />
              <span className="font-bold text-slate-800">KREDİPARK</span>
            </div>
            <button className="p-2 text-slate-600 hover:text-slate-900 relative">
              <Bell size={20} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
          </div>
        </div>

        {/* Page content */}
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;